import { Link, useNavigate } from 'react-router-dom';
import { GlassButton } from '@/components/GlassButton';
import { Reveal } from '@/components/Reveal';
import { SpotlightCard } from '@/components/SpotlightCard';
import { useAuth } from '@/lib/auth';
import { useAppStore } from '@/lib/store';
import { COURSES } from '@/lib/data';

const AVATARS = ['🦊', '🐼', '🐶', '🐱', '🦁', '🐯', '🐻', '🐨', '🐸', '🦉'];

export function ProfilePage(): JSX.Element {
  const { user: authUser, logout } = useAuth();
  const navigate = useNavigate();
  const localUser = useAppStore((s) => s.user);
  // Real account info comes from auth; local store holds display preferences
  // (avatar emoji, etc.) that don't (yet) exist server-side.
  const user = { ...localUser, ...(authUser ?? {}) };
  const progress = useAppStore((s) => s.progress);
  const srs = useAppStore((s) => s.srs);
  const setUser = useAppStore((s) => s.setUser);

  async function handleLogout(): Promise<void> {
    await logout();
    navigate('/', { replace: true });
  }

  const completed = new Set(progress.completedSentenceIds);
  const masteredCourses = COURSES.filter((c) => {
    const total = c.lessons.reduce((acc, l) => acc + l.sentences.length, 0);
    const done = c.lessons.reduce(
      (acc, l) => acc + l.sentences.filter((s) => completed.has(s.id)).length,
      0,
    );
    return total > 0 && done === total;
  }).length;

  const level = Math.max(1, Math.floor(progress.xp / 500) + 1);
  const xpInLevel = progress.xp % 500;
  const xpPct = (xpInLevel / 500) * 100;

  return (
    <div className="space-y-10">
      {/* Profile hero */}
      <Reveal>
        <SpotlightCard className="overflow-hidden">
          <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
            <div className="text-7xl grid place-items-center h-24 w-24 rounded-3xl bg-gradient-to-br from-honey-400/20 to-mint-400/20">
              {user.avatarEmoji}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h1 className="font-display text-3xl font-extrabold text-white">{user.name}</h1>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  user.plan === 'pro'
                    ? 'bg-honey-400/20 text-honey-200 border border-honey-400/40'
                    : 'bg-white/[0.05] text-cream-400 border border-white/[0.08]'
                }`}>
                  {user.plan === 'pro' ? '★ Pro' : 'Free'}
                </span>
              </div>
              <div className="text-sm text-cream-400 mt-1">{user.email}</div>

              <div className="mt-5">
                <div className="flex items-center justify-between text-xs text-cream-400 mb-1.5">
                  <span>Lv.{level}</span>
                  <span>{xpInLevel} / 500 XP · 下一级</span>
                </div>
                <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-honey-400 to-mint-400"
                    style={{ width: `${xpPct}%` }}
                  />
                </div>
              </div>
            </div>
            {user.plan === 'free' && (
              <GlassButton to="/app/plan" size="md" variant="solid">
                ✨ 升级 Pro
              </GlassButton>
            )}
          </div>
        </SpotlightCard>
      </Reveal>

      {/* Stats */}
      <Reveal delay={0.05}>
        <div className="grid gap-4 md:grid-cols-4">
          <StatBlock label="连续学习" value={progress.streakDays} suffix="天" tone="ember" emoji="🔥" />
          <StatBlock label="累计时长" value={progress.totalMinutes} suffix="分钟" tone="honey" emoji="⏱" />
          <StatBlock label="已学句子" value={progress.completedSentenceIds.length} suffix="句" tone="mint" emoji="✓" />
          <StatBlock label="已掌握课程" value={masteredCourses} suffix="门" tone="cream" emoji="🎓" />
        </div>
      </Reveal>

      {/* Settings */}
      <Reveal>
        <div>
          <h2 className="font-display text-2xl font-bold text-white mb-5">个人设置</h2>
          <div className="rounded-3xl border border-white/[0.06] bg-white/[0.02] p-6 space-y-6">
            <div>
              <div className="text-sm text-cream-400 mb-2">显示名称</div>
              <input
                type="text"
                value={user.name}
                onChange={(e) => setUser({ name: e.target.value })}
                className="w-full md:max-w-sm rounded-2xl bg-white/[0.04] border border-white/[0.08] px-4 py-2.5 text-white focus:outline-none focus:border-honey-400/60 focus:bg-white/[0.06] transition-all"
              />
            </div>
            <div>
              <div className="text-sm text-cream-400 mb-3">头像</div>
              <div className="flex flex-wrap gap-2">
                {AVATARS.map((a) => (
                  <button
                    key={a}
                    onClick={() => setUser({ avatarEmoji: a })}
                    className={`text-3xl h-12 w-12 rounded-2xl border transition-all grid place-items-center ${
                      user.avatarEmoji === a
                        ? 'bg-honey-400/20 border-honey-400/60 scale-110'
                        : 'bg-white/[0.03] border-white/[0.06] hover:bg-white/[0.06] hover:border-white/[0.14]'
                    }`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Reveal>

      {/* Achievements (placeholder badges) */}
      <Reveal>
        <div>
          <h2 className="font-display text-2xl font-bold text-white mb-5">成就徽章</h2>
          <div className="grid gap-4 md:grid-cols-4">
            {[
              { emoji: '🌱', label: '初学者', desc: '完成第一节课', got: progress.completedSentenceIds.length > 0 },
              { emoji: '🔥', label: '七日坚持', desc: '连续学习 7 天', got: progress.streakDays >= 7 },
              { emoji: '📚', label: '课程毕业', desc: '完成 1 门完整课程', got: masteredCourses >= 1 },
              { emoji: '✨', label: '千级 XP', desc: '累计 1000 XP', got: progress.xp >= 1000 },
            ].map((b) => (
              <div
                key={b.label}
                className={`rounded-3xl border p-5 text-center transition-all ${
                  b.got
                    ? 'border-honey-400/30 bg-honey-400/[0.06]'
                    : 'border-white/[0.06] bg-white/[0.02] opacity-50'
                }`}
              >
                <div className={`text-5xl mb-2 ${b.got ? '' : 'grayscale'}`}>{b.emoji}</div>
                <div className="font-display font-bold text-white">{b.label}</div>
                <div className="text-xs text-cream-400 mt-1">{b.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      <Reveal>
        <div className="rounded-3xl border border-white/[0.06] bg-white/[0.02] p-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="font-display font-bold text-white">退出登录</div>
            <div className="text-xs text-cream-400 mt-0.5">
              {authUser !== null ? `${authUser.email} · 服务端 session 已注销` : '本地数据存在浏览器'}
            </div>
          </div>
          <div className="flex gap-3">
            {authUser?.role === 'admin' && (
              <Link to="/app/admin" className="text-sm text-honey-300 hover:text-honey-200 self-center">
                🛡️ 后台
              </Link>
            )}
            <Link to="/" className="text-sm text-cream-300 hover:text-white self-center">
              ← 官网
            </Link>
            {authUser !== null && (
              <button
                onClick={() => void handleLogout()}
                className="rounded-full bg-ember-500/15 border border-ember-400/40 px-4 py-2 text-sm text-ember-200 hover:bg-ember-500/25 transition-colors"
              >
                登出
              </button>
            )}
          </div>
        </div>
      </Reveal>
    </div>
  );
}

function StatBlock({
  label,
  value,
  suffix,
  tone,
  emoji,
}: {
  label: string;
  value: number;
  suffix: string;
  tone: 'ember' | 'honey' | 'mint' | 'cream';
  emoji: string;
}): JSX.Element {
  const toneClass =
    tone === 'ember'
      ? 'text-ember-300'
      : tone === 'honey'
        ? 'text-honey-300'
        : tone === 'mint'
          ? 'text-mint-300'
          : 'text-cream-200';
  return (
    <div className="rounded-3xl border border-white/[0.06] bg-white/[0.02] p-5">
      <div className="flex items-center justify-between">
        <span className="text-xl">{emoji}</span>
        <div className="text-right">
          <span className={`font-display text-3xl font-extrabold ${toneClass}`}>{value}</span>
          <span className="text-cream-400 text-sm ml-1">{suffix}</span>
        </div>
      </div>
      <div className="mt-3 text-xs text-cream-400">{label}</div>
    </div>
  );
}
